import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { Gateway } from '../core/services/gateway.service';
import { Configuration } from '../core/configuration';
import { ContentType } from '../core/models/content';
import { Conversation } from '../core/models/conversation';
import { Option } from '../core/models/options-content';
import { Origin } from '../core/models/origin';
import { ConfigurationLoader } from '../core/services/configuration-loader.service';
import { Message } from '../core/models/message';

/**
 * Componente que exibe uma lista de mensagens na tela.
 * 
 * Recebe como @Input uma lista de mensagens e varre toda essa lista, exibindo
 * os Contents de cada mensagem em ordem cronológica.
 * 
 * O aspecto visual e comportamental da lista depende das configurações passadas
 * como entrada no parâmetro "config".
 */
@Component({
  selector: 'serprobots-messages-list',
  templateUrl: 'messages-list.component.html',
  styleUrls: ['messages-list.component.css']
})
export class MessagesListComponent implements OnInit, OnChanges {

  // Lista de mensagens que devem ser exibidas.
  @Input() conversation!: Conversation;

  // Evento emitido sempre que o usuário seleciona uma opção exibida na lista.
  @Output() optionSelected = new EventEmitter<Option>();

  // Evento emitido quando um dos subcomponentes requisita o envio de uma mensagem de texto.
  @Output() requestSendText = new EventEmitter<string>();

  configuration: Configuration = new Configuration({});

  contentImage = ContentType.IMAGE;
  contentText = ContentType.TEXT;
  contentOptions = ContentType.OPTIONS;
  originBot = Origin.BOT;
  originUser = Origin.USER;

  // Define se deve ou não exibir a mensagem de Typing. Usada no Template.
  isSending: boolean = false;

  constructor(
    private configurationLoader: ConfigurationLoader,
    private gateway: Gateway
  ) {}

   /**
   * Carregar as configurações, necessárias para definir comportamentos e visual da tela.
   * 
   * Observação: o await não bloqueia a renderização da tela! Ele é ignorado pelo Angular,
   * portanto, a configuração pode estar nula. Por este motivo, a tela é iniciada com uma configuração vazia.
   */
  async ngOnInit(): Promise<void> {
    this.configuration = await this.configurationLoader.getConfiguration();
  }

  /**
   * Informa ao template qual a classe de tipografia que deve ser usada.
   * 
   * @returns Classe que deve ser usada.
   */
  getClass(): string {
    return `mat-typography ${this.configuration.typography || ""}`;
  }

  /**
   * Informa ao template qual a cor que deve ser usada para o texto da mensagem.
   * 
   * @param message Mensagem.
   * @returns Cor do texto.
   */
  getMessageBoxTextColor(message: Message): string {
    var result = "white";

    if (message.isError) {
      result = this.configuration.errorMessageBoxTextColor;
    } else if (message.origin == this.originUser) {
      result = this.configuration.userMessageBoxTextColor;
    } else if (message.origin == this.originBot) {
      result = this.configuration.botMessageBoxTextColor;
    }

    return result;
  }

  /**
   * Informa ao template qual a cor que deve ser usada para o fundo da mensagem.
   * 
   * @param message Mensagem.
   * @returns Cor do background.
   */
  getMessageBoxBackgroundColor(message: Message): string {
    var result = "white";

    if (message.isError) {
      result = this.configuration.errorMessageBoxColor;
    } else if (message.origin == this.originUser) {
      result = this.configuration.userMessageBoxColor;
    } else if (message.origin == this.originBot) {
      result = this.configuration.botMessageBoxColor;
    }

    return result;
  }
 
  /**
   * Usando o ngOnChanges para adicionar o subscriber para receber as notificações de novas mensagens na conversação.
   * Toda vez que uma nova conversação é "setada", colocamos um novo subscriber para essa nova conversação.
   * 
   * @param changes 
   */
  ngOnChanges(changes: SimpleChanges): void {

    // Observar o Gateway para ser notificado quando houver uma chamada
    // para enviar uma mensagem para o Gateway.
    this.gateway.isSending().subscribe((isSending) => {
      this.isSending = isSending;
    });
  }

  /**
   * Trata o evento que informa que uma opção foi selecionada a partir
   * do componente OptionsContentComponent.
   * 
   * Esse componente é usado para exibir a lista de opções de uma mensagem, caso 
   * esta mensagem seja do tipo ContentType.OPTIONS.
   * 
   * @param option Opção selecionada.
   */
  onOptionSelect(option: Option): void {
    this.optionSelected.emit(option);
  }

  /**
   * Repassa o evento de solicitação de envio de uma mensagem option para os inscritos para receberem esse evento.
   * 
   * @param option Option a ser repassada.
   */
   onRequestSendOption(option: Option): void {
      this.optionSelected.emit(option);
  }
  
  /**
   * Repassa o evento de solicitação de envio de uma mensagem text para os inscritos para receberem esse evento.
   * 
   * @param text Texto a ser repassado.
   */
  onRequestSendText(text: string): void {
    this.requestSendText.emit(text);
  }
  
  /**
   * Obter a mensagem de texto que deve ser exibida toda vez que o bot estiver
   * "digitando" uma mensagem.
   */
  getTypingText(): string {
    var text = this.configuration.typingText || "";
    text = text.replace('{botName}', this.configuration.botName || "Bot");

    return text;
  }

  /**
   * Informa para o template se a mensagem de Typing deve ser exibida.
   * 
   * @returns True caso a mensagem deva ser exibida. False caso contrário.
   */
  showTypingText(): boolean {
    return this.configuration.showIsTyping || true;
  }

  /**
   * Obtém a imagem que deve ser exibida como Avatar do usuário.
   * Suporte inicial apenas a Base64.
   * 
   * @returns Imagem do usuário.
   */
  userAvatar(): string {
    var result = "";

    if (this.configuration.showAvatars) {
      
      // Caso seja para exibir o avatar mas não foi provida nenhuma configuração ou nenhum avatar,
      // vamos utilizar um avatar padrão.
      if (!this.configuration || !this.configuration.userAvatar) {
        // FIXME Guardar esse Base64 em outro local.
        result = "data:image/png;base64,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";
      } else {
        result = this.configuration.userAvatar;
      }
    } else {
      // Isto é uma imagem GIF em branco, apenas para preencher o espaço.
      // FIXME Guardar esse Base64 em outro local.
      result = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVQYV2NgYAAAAAMAAWgmWQ0AAAAASUVORK5CYII=";
    }

    return result;
  }
  
  /**
   * Obtém a imagem que deve ser exibida como Avatar do Bot.
   * Suporte inicial apenas a Base64.
   * 
   * @returns Imagem do usuário.
   */
   botAvatar(): string {
    var result = "";

    if (this.configuration.showAvatars) {

      // Caso seja para exibir o avatar mas não foi provida nenhuma configuração ou nenhum avatar,
      // vamos utilizar um avatar padrão.
      if (!this.configuration || !this.configuration.userAvatar) {
        // FIXME Guardar esse Base64 em outro local.
        result = "data:image/png;base64,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";
      } else {
        result = this.configuration.botAvatar!;
      }
    } else {
      // Isto é uma imagem GIF em branco, apenas para preencher o espaço.
      // FIXME Guardar esse Base64 em outro local.
      result = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVQYV2NgYAAAAAMAAWgmWQ0AAAAASUVORK5CYII=";
    }

    return result;
  }

}
